import { z } from 'zod';
import { createRESTContract } from '../../index';
import {
  BackfillSentimentRequestSchema,
  BackfillSentimentResponseSchema,
  CsatConfigResponseSchema,
  HotTopicsConfigResponseSchema,
  PlatformSettingsSchema,
  RegenerateHotTopicsRequestSchema,
  RegenerateHotTopicsResponseSchema,
  UpdateCsatConfigSchema,
  UpdateHotTopicDescriptionSchema,
  UpdateHotTopicsConfigSchema,
  UpdatePlatformSettingsSchema,
  UpsertHotTopicSchema,
} from './platform-settings.schema';

/**
 * Super-admin only, with no company-scoped permission. The settings pages sit
 * behind a `superAdminOnly` route gate in the frontend
 * (`AuthenticatedAppRoutes.tsx`); `superAdminOnly` here is what enforces the
 * same rule on the API, which the route gate cannot do.
 */
export const platformSettingsContract = createRESTContract({
  getPlatformSettings: {
    method: 'GET',
    path: '/platform-settings',
    superAdminOnly: true,
    response: PlatformSettingsSchema,
  },
  updatePlatformSettings: {
    method: 'PUT',
    path: '/platform-settings',
    superAdminOnly: true,
    body: UpdatePlatformSettingsSchema,
    response: z.void(),
  },
  getHotTopicsConfig: {
    method: 'GET',
    path: '/platform-settings/hot-topics-config',
    superAdminOnly: true,
    response: HotTopicsConfigResponseSchema,
  },
  updateHotTopicsConfig: {
    method: 'PUT',
    path: '/platform-settings/hot-topics-config',
    superAdminOnly: true,
    body: UpdateHotTopicsConfigSchema,
    response: z.void(),
  },
  getCsatConfig: {
    method: 'GET',
    path: '/platform-settings/csat-config',
    superAdminOnly: true,
    response: CsatConfigResponseSchema,
  },
  updateCsatConfig: {
    method: 'PUT',
    path: '/platform-settings/csat-config',
    superAdminOnly: true,
    body: UpdateCsatConfigSchema,
    response: z.void(),
  },
  upsertHotTopic: {
    method: 'PUT',
    path: '/platform-settings/hot-topics-config/topics',
    superAdminOnly: true,
    body: UpsertHotTopicSchema,
    response: z.void(),
  },
  updateHotTopicDescription: {
    method: 'PUT',
    path: '/platform-settings/hot-topics-config/topic-description',
    superAdminOnly: true,
    body: UpdateHotTopicDescriptionSchema,
    response: z.void(),
  },
  deleteHotTopic: {
    method: 'DELETE',
    path: '/platform-settings/hot-topics-config/:topicId',
    superAdminOnly: true,
    body: z.void(),
    response: z.void(),
  },
  // Re-runs the live classifier against every llm_usages row in the given UTC
  // day. Gated by `superAdminOnly` below, like every route in this contract.
  regenerateHotTopicsForDay: {
    method: 'POST',
    path: '/platform-settings/hot-topics-config/regenerate',
    superAdminOnly: true,
    body: RegenerateHotTopicsRequestSchema,
    response: RegenerateHotTopicsResponseSchema,
  },
  backfillSentiment: {
    method: 'POST',
    path: '/platform-settings/sentiment/backfill',
    superAdminOnly: true,
    body: BackfillSentimentRequestSchema,
    response: BackfillSentimentResponseSchema,
  },
});
